import { PillItem } from "../../components/pill"
import { JobbinInputGrey } from "../../components/jobbinInputGrey"
import { JobbinPrimaryButton } from "../../components/primaryButton"
import { useGetSettings } from "../../hooks/employer-hooks"
import { useGetMyGigs } from "../../hooks/gig-hooks"
import { UploadAndDisplayProfileImage } from "../../components/uploadAndDisplayProfileImage"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import * as employerService from "../../services/employer-service"
import { LoadingComponent } from "../../components/loading"
import { useToast } from "../../context/toast"

export const SettingsUpdatePage = () => {
    const navigate = useNavigate()
    const { notify } = useToast()
    const { settings } = useGetSettings()
    const { gigs } = useGetMyGigs()

    const [oldPassword, setOldPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [email, setEmail] = useState("")
    const [phone, setPhone] = useState("")
    const [loading, setLoading] = useState(false)

    const onImageSelected = async (img: File) => {
        setLoading(true)
        try {
            const formData = new FormData()
            formData.append("file", img)
            const response = await employerService.changeProfilePicture(formData)
            if (response.ok) {
                navigate("/settings")
            } else {
                notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
            }
        } catch {
            notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
        } finally {
            setLoading(false)
        }
    }

    let myGigTitles: string[] = []

    gigs.forEach((gig) => {
        myGigTitles.push(gig.gigTitle)
        myGigTitles = myGigTitles.filter((item, index, self) => self.indexOf(item) === index)
        if (myGigTitles.length > 4) {
            myGigTitles = myGigTitles.slice(0, 3)
        }
    })

    const redirectToAddJob = () => {
        navigate("/post-job")
    }

    const doApplyChanges = async () => {
        // Change password
        if (oldPassword || newPassword || confirmPassword) {
            if (!oldPassword || !newPassword || !confirmPassword) {
                notify("Completează toate câmpurile pentru schimbarea parolei!", "info")
                return
            }
            if (newPassword !== confirmPassword) {
                notify("Parolele noi nu coincid!", "error")
                return
            }

            const response = await employerService.updatePassword({
                oldPassword,
                newPassword,
                confirmPassword,
            })

            if (!response.ok) {
                notify("Eroare la schimbarea parolei! Verifică parola veche.", "error")
                return
            }
        }

        // Change email + phone via updateProfileSettings
        if (email || phone) {
            const updateRequest: any = {}
            if (email) updateRequest.email = email
            if (phone) updateRequest.phone = phone

            const response = await employerService.updateProfileSettings(updateRequest)

            if (!response.ok) {
                notify("Eroare la actualizarea datelor! Te rugăm să contactezi suportul.", "error")
                return
            }
        }

        notify("Schimbările au fost aplicate cu succes!", "success")
        navigate("/settings")
    }

    if (loading) {
        return (
            <div>
                <LoadingComponent />
            </div>
        )
    }

    return (
        <div id="settings-page" className="w-full px-4 py-4 sm:px-6 sm:py-6">
            <div
                id="settings-bar"
                className="flex flex-col gap-4 rounded-[16px] bg-figmaLightBg p-4 sm:p-6 xl:flex-row xl:items-center xl:justify-between"
            >
                <div className="shrink-0">
                    {settings ? (
                        <UploadAndDisplayProfileImage imageUrl={settings.profilePictureUrl} onImageSelected={onImageSelected} />
                    ) : null}
                </div>

                <div
                    id="available-job-title"
                    className="flex flex-col gap-3 xl:flex-row xl:items-center xl:border-l-2 xl:border-white xl:pl-6"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        {myGigTitles.map((pozitie, index) => (
                            <PillItem key={`${pozitie}-${index}`} text={pozitie} classes="" />
                        ))}
                    </div>

                    <div
                        id="plus-icon"
                        className="flex h-11 w-11 items-center justify-center rounded-full bg-white cursor-pointer self-start xl:self-auto"
                        onClick={redirectToAddJob}
                    >
                        <img src="/images/add-plus-icon.svg" alt="Adaugă job" />
                    </div>
                </div>
            </div>

            <div
                id="change-details"
                className="mt-6 flex flex-col gap-6 xl:grid xl:grid-cols-2 xl:gap-8"
            >
                <div id="password-details" className="w-full">
                    <h1 className="jobbin-input text-[24px] sm:text-[28px] lg:text-[32px] text-figmaBlack">
                        Schimbă parola
                    </h1>

                    <div id="old-password" className="mt-5">
                        <JobbinInputGrey
                            type="password"
                            iconStart="locket.svg"
                            label="Parola veche"
                            name="old-password"
                            value={oldPassword}
                            placeholder="Parola ta"
                            onChange={(e) => setOldPassword((e.target as HTMLInputElement).value)}
                        />
                    </div>

                    <div id="new-password" className="mt-5">
                        <JobbinInputGrey
                            type="password"
                            iconStart="category-icon.svg"
                            label="Parola nouă"
                            name="new-password"
                            value={newPassword}
                            placeholder="Parola ta"
                            onChange={(e) => setNewPassword((e.target as HTMLInputElement).value)}
                        />
                    </div>

                    <div id="confirm-password" className="mt-5">
                        <JobbinInputGrey
                            type="password"
                            iconStart="location-icon.svg"
                            label="Confirmă parola nouă"
                            name="confirm-password"
                            value={confirmPassword}
                            placeholder="Parola ta"
                            onChange={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
                        />
                    </div>
                </div>

                <div id="email-phone-details" className="w-full">
                    <h1 className="jobbin-input text-[24px] sm:text-[28px] lg:text-[32px] text-figmaBlack">
                        Schimbă emailul
                    </h1>

                    <div id="email-change" className="mt-5">
                        <JobbinInputGrey
                            type="email"
                            iconStart="mail-icon.svg"
                            label="Email"
                            name="new-email"
                            value={email}
                            placeholder="Introdu adresa de email nouă"
                            onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                        />
                    </div>

                    <h1 className="mt-8 sm:mt-10 lg:mt-12 jobbin-input text-[24px] sm:text-[28px] lg:text-[32px] text-figmaBlack">
                        Schimbă telefonul
                    </h1>

                    <div id="phone-change" className="mt-5">
                        <JobbinInputGrey
                            type="phone"
                            iconStart="phone-receptor.svg"
                            label="Număr de telefon"
                            name="new-phone"
                            value={phone}
                            placeholder="Introdu numărul de telefon nou"
                            onChange={(e) => setPhone((e.target as HTMLInputElement).value)}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-8 sm:mt-10 lg:mt-12 flex justify-stretch sm:justify-end">
                <div className="w-full sm:w-auto sm:min-w-[220px]">
                    <JobbinPrimaryButton onClickAction={doApplyChanges} text="Aplică schimbări" />
                </div>
            </div>
        </div>
    )
}